import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CalendarClock,
  CheckCircle,
  Database,
  FileCheck,
  FileText,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
  X,
  XCircle,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import PageHeader from '../components/PageHeader';

const Pill = ({ icon: Icon, label, className }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${className}`}>
    <Icon size={16} />
    <span>{label}</span>
  </div>
);

const Tile = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <div className="text-lg font-bold text-gray-900 mb-2">{title}</div>
    <div className="text-sm text-gray-600 leading-relaxed">{description}</div>
  </div>
);

const StepCard = ({ index, icon: Icon, title, description }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-7 h-full">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#141414] text-white flex items-center justify-center font-extrabold">
          {String(index).padStart(2, '0')}
        </div>
        <div className="p-3 rounded-2xl bg-gray-50 border border-gray-200 text-gray-900">
          <Icon size={22} />
        </div>
      </div>
      <div className="px-3 py-1 rounded-full text-xs font-bold border bg-emerald-50 border-emerald-200 text-emerald-800">
        Journey
      </div>
    </div>
    <div className="mt-5 text-xl font-extrabold text-[#141414]">{title}</div>
    <div className="mt-3 text-sm text-gray-600 leading-relaxed">{description}</div>
  </div>
);

const CompareRow = ({ label, traditional, digital }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl px-5 py-4">
      <div className="text-sm font-extrabold text-[#141414]">{label}</div>
    </div>
    <div className="lg:col-span-4 bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
      <div className="flex items-start gap-3">
        <XCircle size={18} className="text-gray-500 mt-0.5" />
        <div className="text-sm text-gray-700 leading-relaxed">{traditional}</div>
      </div>
    </div>
    <div className="lg:col-span-5 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
      <div className="flex items-start gap-3">
        <CheckCircle size={18} className="text-emerald-700 mt-0.5" />
        <div className="text-sm text-emerald-900 leading-relaxed">{digital}</div>
      </div>
    </div>
  </div>
);

const MetricCard = ({ value, title, text, tone }) => (
  <div
    className={`rounded-3xl border p-6 bg-white ${
      tone === 'good' ? 'border-emerald-200' : tone === 'accent' ? 'border-amber-200' : 'border-gray-200'
    }`}
  >
    <div className="text-3xl font-extrabold text-[#141414]">{value}</div>
    <div className="mt-2 text-sm font-extrabold uppercase tracking-widest text-gray-500">{title}</div>
    <div className="mt-3 text-sm text-gray-700 leading-relaxed">{text}</div>
  </div>
);

const FaqItem = ({ q, answer }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left hover:bg-gray-50 transition"
      >
        <div className="text-sm md:text-base font-extrabold text-[#141414]">{q}</div>
        <div className="shrink-0 text-gray-500">{open ? '−' : '+'}</div>
      </button>
      {open && <div className="px-6 pb-6 text-sm text-gray-700 leading-relaxed">{answer}</div>}
    </div>
  );
};

const ClinicasPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const heroImageUrl = '/images/clinicas/clinica.png';

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const journey = useMemo(() => {
    const items = t('clinicas.journey_section.steps', { returnObjects: true });
    const icons = [CalendarClock, ShieldCheck, Activity, FileText, Database];
    return items.map((item, i) => ({ ...item, icon: icons[i], description: item.desc }));
  }, [t]);

  const modules = useMemo(() => {
    const items = t('clinicas.modules_section.items', { returnObjects: true });
    const icons = [Users, Database, FileText, Workflow];
    const colors = [
      { bg: 'bg-blue-50', text: 'text-blue-700' },
      { bg: 'bg-gray-50', text: 'text-gray-700' },
      { bg: 'bg-amber-50', text: 'text-amber-700' },
      { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    ];
    return items.map((item, i) => ({ ...item, icon: icons[i], description: item.desc, color: colors[i] }));
  }, [t]);

  const comparisons = useMemo(() => {
    const items = t('clinicas.comparison_section.items', { returnObjects: true });
    return items.map(item => ({
      label: item.label,
      traditional: item.trad,
      digital: item.digi
    }));
  }, [t]);

  const kpis = useMemo(() => {
    const items = t('clinicas.benefits_section.kpis', { returnObjects: true });
    const tones = ['good', 'accent', 'neutral', 'neutral'];
    return items.map((item, i) => ({ ...item, text: item.desc, tone: tones[i] }));
  }, [t]);

  const faqs = useMemo(() => {
    const raw = t('clinicas.faq_section.items', { returnObjects: true });
    const list = Array.isArray(raw) ? raw : Object.values(raw || {});
    return list.map((item) => ({
      q: item?.q || item?.question || '',
      answer: item?.a || item?.desc || item?.answer || '',
    }));
  }, [t]);

  return (
    <div className="relative min-h-screen bg-transparent">
      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label="Cerrar popup"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('clinicas.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('clinicas.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-[85vh]">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('clinicas.back')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt="Gestión Clínica 360"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Clinicas';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20">
              <div className="grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="flex flex-wrap gap-2">
                    <Pill icon={Workflow} label={t('clinicas.badge')} className="bg-white/10 border-white/15 text-white" />
                    <Pill
                      icon={Sparkles}
                      label={t('clinicas.badges.automation')}
                      className="bg-amber-500/10 border-amber-500/25 text-amber-200"
                    />
                    <Pill
                      icon={ShieldCheck}
                      label={t('clinicas.badges.security')}
                      className="bg-emerald-500/10 border-emerald-500/25 text-emerald-200"
                    />
                  </div>

                  <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                    {t('clinicas.title')}
                  </h1>
                  <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl">
                    {t('clinicas.subtitle')}
                  </p>

                  <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={openPopup}
                      className="inline-flex items-center justify-center gap-2 bg-white text-[#141414] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition"
                    >
                      {t('common.free_diagnostic')} <ArrowRight size={18} />
                    </button>
                    <a
                      href="#journey"
                      className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/15 transition"
                    >
                      {t('clinicas.cta_journey')} <ArrowDown size={18} />
                    </a>
                  </div>

                  <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl">
                    {t('clinicas.stats', { returnObjects: true }).map((it) => (
                      <div key={it.label} className="bg-white/10 border border-white/15 rounded-2xl p-4">
                        <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{it.label}</div>
                        <div className="mt-2 text-sm text-white/80">{it.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="bg-white/10 border border-white/15 backdrop-blur rounded-3xl p-7">
                    <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{t('clinicas.quick_map.title')}</div>
                    <div className="mt-3 text-2xl font-extrabold text-white">{t('clinicas.quick_map.subtitle')}</div>
                    <div className="mt-6 space-y-3">
                      {journey.map((j) => (
                        <div key={j.title} className="flex items-start gap-3 bg-black/20 border border-white/10 rounded-2xl p-4">
                          <div className="p-3 rounded-2xl bg-white/10 border border-white/15 text-white">
                            <j.icon size={20} />
                          </div>
                          <div>
                            <div className="text-sm font-extrabold text-white">{j.title}</div>
                            <div className="text-sm text-white/70 leading-relaxed">{j.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="journey" className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{t('clinicas.journey_section.title')}</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {t('clinicas.journey_section.subtitle')}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {t('clinicas.journey_section.desc')}
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-6 items-stretch">
            {journey.map((s, idx) => (
              <StepCard key={s.title} index={idx + 1} icon={s.icon} title={s.title} description={s.description} />
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{t('clinicas.modules_section.title')}</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {t('clinicas.modules_section.subtitle')}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {t('clinicas.modules_section.desc')}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((m) => (
              <Tile key={m.title} icon={m.icon} title={m.title} description={m.description} color={m.color} />
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">
              {t('clinicas.comparison_section.title')}
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {t('clinicas.comparison_section.subtitle')}
            </h2>
          </div>

          <div className="mt-10 bg-white border border-gray-200 rounded-3xl p-6 md:p-8">
            <div className="hidden lg:grid grid-cols-12 gap-4 mb-6">
              <div className="col-span-3" />
              <div className="col-span-4">
                <div className="bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
                  <div className="text-sm font-extrabold text-gray-900">{t('clinicas.comparison_section.conventional')}</div>
                </div>
              </div>
              <div className="col-span-5">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
                  <div className="text-sm font-extrabold text-emerald-900">{t('clinicas.comparison_section.bitrix24')}</div>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {comparisons.map((r) => (
                <CompareRow key={r.label} label={r.label} traditional={r.traditional} digital={r.digital} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">
                  {t('clinicas.benefits_section.title')}
                </div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-[#141414]">
                  {t('clinicas.benefits_section.subtitle')}
                </h2>
                <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                  {t('clinicas.benefits_section.desc')}
                </p>

                <div className="mt-8 grid gap-3">
                  {t('clinicas.benefits_section.points', { returnObjects: true }).map((text, i) => {
                    const icons = [FileCheck, Workflow, ShieldCheck];
                    const Icon = icons[i];
                    return (
                      <div key={text} className="flex items-start gap-3">
                        <Icon size={18} className="text-emerald-700 mt-0.5" />
                        <div className="text-sm text-gray-700 leading-relaxed">{text}</div>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={openPopup}
                  className="mt-8 inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-900 transition"
                >
                  {t('clinicas.benefits_section.cta')} <ArrowRight size={18} />
                </button>
              </div>

              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                {kpis.map((k) => (
                  <MetricCard key={k.title} value={k.value} title={k.title} text={k.text} tone={k.tone} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{t('clinicas.faq_section.title')}</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {t('clinicas.faq_section.subtitle')}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {t('clinicas.faq_section.desc')}
            </p>
          </div>

          <div className="mt-10 grid gap-4 max-w-4xl mx-auto">
            {faqs.map((f) => (
              <FaqItem key={f.q} q={f.q} answer={f.answer} />
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="bg-[#141414] rounded-3xl p-9 md:p-12 text-white overflow-hidden relative">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-emerald-500/20 blur-2xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{t('clinicas.final_cta.badge')}</div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">
                  {t('clinicas.final_cta.title')}
                </h2>
                <p className="mt-5 text-white/75 text-lg leading-relaxed">
                  {t('clinicas.final_cta.subtitle')}
                </p>
                <div className="mt-7 flex flex-wrap gap-2">
                  <Pill icon={MessageCircle} label="WhatsApp" className="bg-white/10 border-white/15 text-white" />
                  <Pill icon={Database} label="Drive" className="bg-white/10 border-white/15 text-white" />
                  <Pill icon={BarChart3} label="KPIs" className="bg-white/10 border-white/15 text-white" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white/10 border border-white/15 rounded-3xl p-7">
                  <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{t('clinicas.final_cta.cta_label')}</div>
                  <div className="mt-4 text-2xl font-extrabold">{t('clinicas.final_cta.card_title')}</div>
                  <div className="mt-3 text-white/75 leading-relaxed">
                    {t('clinicas.final_cta.card_desc')}
                  </div>
                  <button
                    onClick={openPopup}
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-white text-[#141414] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition"
                  >
                    {t('clinicas.final_cta.btn')} <ArrowRight size={18} />
                  </button>
                  <div className="mt-3 text-xs text-white/60 text-center">
                    {t('clinicas.final_cta.note')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ClinicasPage;

